import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, KeyRound, ArrowLeft } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { isValidMobile, MOBILE_ERROR } from "@/lib/validation";

export default function ProfileEdit() {
  const { customerInfo, setCustomerAuth, customerToken, logoutCustomer } =
    useApp();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    name: customerInfo?.name || "",
    email: customerInfo?.email || "",
    mobile: customerInfo?.mobile || "",
    whatsapp: customerInfo?.whatsapp || "",
    saved_venue: customerInfo?.saved_venue || "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [savingP, setSavingP] = useState(false);
  const [savingW, setSavingW] = useState(false);

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setPw = (k, v) => setPwd((p) => ({ ...p, [k]: v }));

  const saveProfile = async (e) => {
    e.preventDefault();
    if (profile.mobile.trim() !== (customerInfo?.mobile || "") && !isValidMobile(profile.mobile)) {
      toast.error(MOBILE_ERROR);
      return;
    }
    setSavingP(true);
    try {
      const payload = {};
      ["name", "email", "mobile", "whatsapp", "saved_venue"].forEach((k) => {
        const val = profile[k];
        const orig = customerInfo?.[k] || "";
        if (val !== orig) payload[k] = val || null;
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setSavingP(false);
        return;
      }
      const { data } = await api.patch("/auth/customer/me", payload);
      setCustomerAuth(customerToken, data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(errMsg(err, "Update failed"));
    } finally {
      setSavingP(false);
    }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setSavingW(true);
    try {
      await api.post("/auth/change-customer-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("Password updated");
    } catch (err) {
      toast.error(errMsg(err, "Change failed"));
    } finally {
      setSavingW(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="container-x py-10">
        <Link
          to="/account"
          data-testid="profile-back"
          className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </Link>

        <div className="max-w-2xl">
          <span className="kbd-eyebrow">Profile</span>
          <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary sm:text-4xl">
            Edit profile
          </h1>
        </div>

        <form
          onSubmit={saveProfile}
          className="surface-card mt-8 space-y-4"
          data-testid="profile-form"
        >
          <F label="Full name">
            <input
              data-testid="profile-name"
              value={profile.name}
              onChange={(e) => setP("name", e.target.value)}
              className="pk-input"
            />
          </F>
          <F label="Email">
            <input
              type="email"
              data-testid="profile-email"
              value={profile.email}
              onChange={(e) => setP("email", e.target.value)}
              className="pk-input"
            />
          </F>
          <F label="Mobile">
            <input
              type="tel"
              data-testid="profile-mobile"
              value={profile.mobile}
              onChange={(e) => setP("mobile", e.target.value)}
              inputMode="numeric"
              maxLength={10}
              placeholder="9812345678"
              className="pk-input"
            />
            {profile.mobile.trim() !== (customerInfo?.mobile || "") && !isValidMobile(profile.mobile) && (
              <span data-testid="profile-mobile-error" className="mt-1 block text-xs font-medium text-destructive">
                {MOBILE_ERROR}
              </span>
            )}
          </F>
          <F label="WhatsApp">
            <input
              type="tel"
              data-testid="profile-whatsapp"
              value={profile.whatsapp}
              onChange={(e) => setP("whatsapp", e.target.value)}
              className="pk-input"
            />
          </F>
          <F label="Saved venue (for future events)">
            <textarea
              rows={2}
              data-testid="profile-venue"
              value={profile.saved_venue}
              onChange={(e) => setP("saved_venue", e.target.value)}
              className="pk-input"
            />
          </F>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingP}
              data-testid="profile-save"
              className="btn-primary"
            >
              <Save className="h-4 w-4" />
              {savingP ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>

        <form
          onSubmit={changePwd}
          className="surface-card mt-8 space-y-4"
          data-testid="change-password-form"
        >
          <h2 className="font-serif-display text-lg font-semibold text-secondary">
            Change password
          </h2>
          <F label="Current password">
            <input
              type="password"
              data-testid="change-pwd-current"
              value={pwd.current_password}
              onChange={(e) => setPw("current_password", e.target.value)}
              required
              autoComplete="current-password"
              className="pk-input"
            />
          </F>
          <F label="New password">
            <input
              type="password"
              data-testid="change-pwd-new"
              value={pwd.new_password}
              onChange={(e) => setPw("new_password", e.target.value)}
              required
              autoComplete="new-password"
              className="pk-input"
            />
          </F>
          <p className="text-[10px] text-muted-foreground">
            At least 8 characters.
          </p>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingW}
              data-testid="change-pwd-submit"
              className="btn-primary"
            >
              <KeyRound className="h-4 w-4" />
              {savingW ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>

        <div className="mt-8 flex justify-end">
          <button
            data-testid="profile-logout"
            onClick={() => {
              logoutCustomer();
              navigate("/");
            }}
            className="text-sm font-semibold text-destructive hover:underline"
          >
            Log out
          </button>
        </div>
      </div>
      <style>{`.pk-input { width:100%; border-radius:0.75rem; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.65rem 1rem; font-size:0.875rem; }
      .pk-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary) / 0.15); }`}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
